<template>
  <el-dialog
    v-model="visible"
    class="growth-popup-dialog"
    modal-class="dialog-m-center"
    width="380px"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    append-to-body
    destroy-on-close
  >
    <div v-if="currentItem" class="growth-popup-shell position-r w-380px h-476px">
      <button
        v-if="popupItems.length > 1"
        type="button"
        class="growth-popup-arrow position-a z-2 flex items-center justify-center w-92px h-92px border-none bg-transparent cursor-pointer"
        aria-label="上一个弹窗"
        @click.stop="goPrev"
      >
        <img src="@/image/bg-left.svg" class="w-40px h-40px" alt="">
      </button>

      <button
        v-if="popupItems.length > 1"
        type="button"
        class="growth-popup-arrow position-a -right-106px top-192px z-2 flex items-center justify-center w-92px h-92px border-none bg-transparent cursor-pointer"
        aria-label="下一个弹窗"
        @click.stop="goNext"
      >
        <img src="@/image/bg-right.svg" class="w-40px h-40px" alt="">
      </button>

      <button
        type="button"
        class="growth-popup-close position-a -right-40px -top-52px z-3 flex items-center justify-center w-36px h-36px border-none bg-transparent cursor-pointer"
        aria-label="关闭弹窗"
        @click.stop="finish"
      >
        <img src="@/image/bg-close.svg" class="w-36px h-36px" alt="">
      </button>

      <button
        :key="currentItem.mockKey || currentItem.popupId"
        type="button"
        class="growth-popup-card position-r block w-380px h-476px overflow-hidden rounded-20px border-none bg-212121 text-left text-white cursor-pointer"
        @click="onCardClick(currentItem)"
      >
        <span v-if="currentItem.title" class="growth-popup-title position-a left-32px top-40px right-32px truncate text-20px lh-26px text-9b">
          {{ currentItem.title }}
        </span>
        <span v-if="currentItem.subtitle" class="growth-popup-subtitle position-a left-32px top-78px right-32px z-1 text-32px lh-40px weight-700 text-white">
          {{ currentItem.subtitle }}
        </span>
        <img
          v-if="currentItem.iconUrl"
          class="growth-popup-icon position-a right-40px bottom-42px w-168px h-auto"
          :src="currentItem.iconUrl"
          alt=""
          @load="onImageLoaded(currentItem)"
        >
        <span class="position-a left-32px bottom-42px text-12px lh-16px text-9b">
          <span class="text-white">{{ activeIndex + 1 }}</span>/{{ popupItems.length }}
        </span>
      </button>
    </div>
  </el-dialog>
</template>

<script setup>
import { reportGrowthPopupClick, getGrowthPopupDeviceId } from '@/utils/analytics/growthClickReport.js'

const emit = defineEmits(['end'])
const configStore = useConfigStore()
const deviceId = getGrowthPopupDeviceId()
const MOCK_POPUP_COUNT = 3

const { payload: popupPayload, isLoading } = configApi.growthPopupList({
  position: 'pc_home',
  displayTarget: 1,
  language: configStore.locale,
  deviceId,
})

const sourcePopupItems = $computed(() => {
  if (!Array.isArray(popupPayload.value)) return []

  return popupPayload.value
    .filter(item => Number(item?.popupId) > 0)
    .slice(0, 3)
})

const popupItems = $computed(() => {
  if (!import.meta.env.DEV || sourcePopupItems.length !== 1) {
    return sourcePopupItems
  }

  return Array.from({ length: MOCK_POPUP_COUNT }, (_, index) => ({
    ...sourcePopupItems[0],
    mockKey: `growth-popup-mock-${index}`,
    isMock: true,
  }))
})

let activeIndex = $ref(0)
let visible = $ref(false)
let finished = false
const exposedIds = new Set()
const loadedImageIds = new Set()

const currentItem = $computed(() => popupItems[activeIndex] || null)

const exposeItem = (item) => {
  const itemId = item?.popupId
  if (item?.isMock || !itemId || exposedIds.has(itemId)) return

  exposedIds.add(itemId)
  const { execute } = configApi.growthPopupExpose({
    itemId,
    platform: 'web',
    deviceId,
    exposeTime: Date.now(),
  })

  execute().catch(error => {
    console.log('growth popup expose report failed', error)
  })
}

const markCurrentItemReady = () => {
  const item = currentItem
  if (!item || (item.iconUrl && !loadedImageIds.has(item.popupId))) return
  exposeItem(item)
}

const onImageLoaded = (item) => {
  loadedImageIds.add(item.popupId)
  if (currentItem?.popupId === item.popupId) {
    exposeItem(item)
  }
}

const finish = () => {
  if (finished) return
  finished = true
  visible = false
  emit('end')
}

const goPrev = () => {
  if (popupItems.length < 2) return
  activeIndex = activeIndex === 0 ? popupItems.length - 1 : activeIndex - 1
  nextTick(markCurrentItemReady)
}

const goNext = () => {
  if (popupItems.length < 2) return
  activeIndex = activeIndex === popupItems.length - 1 ? 0 : activeIndex + 1
  nextTick(markCurrentItemReady)
}

const nativeRouteMap = {
  FBJY: '/c2c',
  HYJY: '/futures',
  XHJY: '/trade',
  YQJL: '/rebate',
  LXKF: '/help/content',
  CZ: '/wallet/recharge',
  LC: '/financial',
  COUPON: '/psersonalCenter/couponCenter',
  SD: '/coinStockConvert',
  ECO: '/ecoV2/ecoHome',
  ECOV2: '/ecoV2/ecoHome',
  ECO_TWO: '/ecoV2/ecoHome',
  ECO2: '/ecoV2/ecoHome',
  ECO_HASHRATE: '/ecoV2/ecoHome',
  ECOTWO: '/ecoV2/ecoHome',
  BIFUND: '/bffund',
  BF: '/bffund',
}

const normalizeKey = value => String(value || '').trim().replace(/^\/+/, '').toUpperCase()

const getNativeKey = item => {
  const appKey = normalizeKey(item?.appKey)
  const jumpTarget = normalizeKey(item?.jumpTarget)
  if (nativeRouteMap[appKey]) return appKey
  if (nativeRouteMap[jumpTarget]) return jumpTarget
  return ''
}

const isHttpUrl = value => {
  try {
    const url = new URL(value)
    return ['http:', 'https:'].includes(url.protocol) && !!url.host
  } catch {
    return false
  }
}

const getRedirectTarget = path => {
  if (typeof path === 'string') return path
  const query = new URLSearchParams(path.query || {}).toString()
  return query ? `${path.path}?${query}` : path.path
}

const openRoute = (path, requireLogin = false) => {
  toRouter(path, {
    requireLogin,
    redirect: requireLogin ? getRedirectTarget(path) : undefined,
    addUrlQuery: false,
  })
  return true
}

const openInnerLink = (target, requireLogin = false) => {
  const normalizedTarget = target.toUpperCase()
  const innerRouteMap = {
    '/MAIN/LXKF': null,
    '/USER/YQJL': '/rebate',
    '/USER/CZ': '/wallet/recharge',
    '/USER/LC': '/financial',
    '/USER/BIFUND': '/bffund',
  }
  const matchedPath = Object.keys(innerRouteMap).find(path => normalizedTarget.endsWith(path))
  if (!matchedPath) return false

  if (matchedPath === '/MAIN/LXKF') {
    if (typeof window.zE === 'function') {
      window.zE('messenger', 'open')
      return true
    }
    return openRoute('/help/content')
  }

  return openRoute(innerRouteMap[matchedPath], requireLogin)
}

const openTarget = (target, requireLogin = false) => {
  if (!target) return false

  if (isHttpUrl(target)) {
    window.open(target, '_blank', 'noopener,noreferrer')
    return true
  }

  if (target.startsWith('inner://')) {
    return openInnerLink(target, requireLogin)
  }

  if (target.startsWith('/')) {
    return openRoute(target, requireLogin)
  }

  return false
}

const normalizeTradeSymbol = (target, isContract) => {
  const symbol = target.replace(/^CONTRACT\//i, '').trim().toUpperCase()
  if (!symbol) return ''
  return isContract ? symbol.replace(/[-_/\s]+/g, '') : symbol.replace(/[-_\s]+/g, '/')
}

const openTrade = (target, isContract, requireLogin = false) => {
  const symbol = normalizeTradeSymbol(target, isContract)
  if (!symbol) return

  openRoute({
    path: isContract ? '/futures' : '/trade',
    query: { symbol: isContract ? symbol.replace(/([A-Z0-9]+)(USDT|USDC|USD)$/, '$1/$2') : symbol },
  }, requireLogin)
}

const openCustomerService = () => {
  if (typeof window.zE === 'function') {
    window.zE('messenger', 'open')
    return true
  }
  return openRoute('/help/content')
}

const openJump = (item) => {
  const type = Number(item?.jumpType)
  const target = String(item?.jumpTarget || '').trim()
  const nativeKey = getNativeKey(item)
  const requireLogin = Number(item?.loginStatus) === 1 ||
    type === 22 ||
    ['ECO', 'ECOV2', 'ECO_TWO', 'ECO2', 'ECO_HASHRATE', 'ECOTWO'].includes(nativeKey)

  switch (type) {
    case 0:
    case 2:
      if (nativeKey === 'LXKF') {
        openCustomerService()
        return
      }
      if (nativeRouteMap[nativeKey]) {
        openRoute(nativeRouteMap[nativeKey], requireLogin)
        return
      }
      openTarget(target, requireLogin)
      return
    case 1:
      if (isHttpUrl(target)) openTarget(target, requireLogin)
      return
    case 3:
      openTrade(target, false, requireLogin)
      return
    case 4:
      openTrade(target, true, requireLogin)
      return
    case 5:
      openRoute('/wallet/recharge', true)
      return
    case 6:
      openRoute('/wallet/withdrawOut', true)
      return
    case 7:
    case 15:
      openTarget(target, requireLogin)
      return
    case 8:
      openRoute('/wallet/walletOverview', true)
      return
    case 9:
      openCustomerService()
      return
    case 10:
      return
    case 11:
      if (!openTarget(target, requireLogin)) openRoute('/financial', requireLogin)
      return
    case 12:
      openRoute('/psersonalCenter/authentication', true)
      return
    case 13:
      openRoute('/rebate', true)
      return
    case 14:
      openRoute('/ieo', requireLogin)
      return
    case 16:
      return
    case 17:
      if (!openTarget(target, requireLogin)) openRoute('/home', requireLogin)
      return
    case 18:
      openRoute('/psersonalCenter/couponCenter', true)
      return
    case 19:
      openRoute('/ipo')
      return
    case 20:
      openRoute('/coinStock')
      return
    case 21:
      openRoute('/superNode')
      return
    case 22:
      openRoute('/ecoV2/ecoHome', true)
      return
    case 23:
      openRoute('/bffund', requireLogin)
      return
    default:
      return
  }
}

const onCardClick = (item) => {
  if (!item?.isMock) {
    reportGrowthPopupClick({ itemId: item?.popupId })
  }
  finish()
  openJump(item)
}

watch([() => popupItems, isLoading], ([items, loading]) => {
  if (loading) return

  if (!items.length) {
    finish()
    return
  }

  activeIndex = 0
  visible = true
  nextTick(markCurrentItemReady)
}, { immediate: true })
</script>

<style lang="scss">
.growth-popup-dialog {
  margin: 0;
  padding: 0;
  background: transparent;
  box-shadow: none;
  overflow: visible;

  .el-dialog__header,
  .el-dialog__body {
    margin: 0;
    padding: 0;
  }
}

.growth-popup-shell {
  overflow: visible;
}

.growth-popup-card {
  font-family: inherit;
}

.growth-popup-subtitle {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.growth-popup-arrow:first-child {
  left: -106px;
  top: 192px;
}
</style>
